import { Users } from "lucide-react";
import { useFormContext } from "react-hook-form";

import { Form } from "@/app/_module/components/ui/form";
import { Input } from "@/app/_module/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/app/_module/components/ui/select";

import { maritalStatusOptions, type MaritalStatus } from "../constants/form-options";
import { type UpdateLeadSchema } from "../schema/lead-schema";

import { FormSection } from "./FormSection";

interface FamilySectionProps {
  isEditing?: boolean; // Make optional since always editing now
  leadData?: any; // Add leadData to check if beneficiary
  bare?: boolean; // When true, render fields only without FormSection/Form wrapper
}

export const FamilySection = function({ isEditing = true, leadData, bare = false }: FamilySectionProps) {
  const form = useFormContext<UpdateLeadSchema>();

  const {
    formState: { errors },
    watch,
    setValue,
    register,
  } = form;

  const marital = watch("marital");
  const relationship = watch("relationship");

  // Check if this lead is a beneficiary (has parentLeadId or isBeneficiary flag)
  const isBeneficiary = leadData?.parentLeadId || leadData?.isBeneficiary;

  const fields = (
        <div className="space-y-4">
          <div className="space-y-2">
            <div className="text-sm font-medium text-gray-700">
              Marital Status
            </div>
            <Select
              disabled={!isEditing}
              onValueChange={(value: MaritalStatus) => setValue("marital", value)}
              value={marital || ""}
            >
              <SelectTrigger className={`${errors.marital ? "border-red-500" : ""} ${!isEditing ? "opacity-60 cursor-not-allowed" : ""}`}>
                <SelectValue placeholder="Select marital status" />
              </SelectTrigger>
              <SelectContent>
                {maritalStatusOptions.map((status) => (
                  <SelectItem key={status} value={status}>
                    {status}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
            {!!errors.marital && <p className="text-sm text-red-600">{errors.marital.message}</p>}
          </div>

          {/* Show Relationship field only for beneficiaries */}
          {isBeneficiary && (
            <div className="space-y-2">
              <div className="text-sm font-medium text-gray-700">
                Relationship to Parent Lead
              </div>
              <Input
                {...register("relationship")}
                placeholder="Son, Daughter, Spouse"
                disabled={!isEditing}
                className={`${errors.relationship ? "border-red-500" : ""} ${!isEditing ? "opacity-60 cursor-not-allowed" : ""}`}
                onChange={(e) => {
                  // Only allow letters and spaces
                  const value = e.target.value.replace(/[^a-zA-Z\s]/g, '');
                  setValue("relationship", value);
                }}
              />
              {!!errors.relationship && <p className="text-sm text-red-600">{errors.relationship.message}</p>}
            </div>
          )}
        </div>
  );

  if (bare) return fields;

  return (
    <FormSection icon={Users} title="Family Information">
      <Form {...form}>
        {fields}
      </Form>
    </FormSection>
  );
};